/**
 * `/settings/departments` — how Sage departments become runway tiles. Sage's hierarchy is followed
 * automatically: a department no custom tile lists rolls up into its top-level Sage parent's tile
 * (fpa-backend ADR-0005). Custom tiles are the exceptions Sage does not model (like CO+MC): rename,
 * merge, reorder or hide. Edits are a local draft saved as a whole after a confirmation that lists
 * the changes (`PUT /v1/settings/departments`). Needs `canConfigure` (admin, manager).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { errorMessage } from '@/api/client'
import type { DepartmentSettings, SageDepartment } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { HowItWorks } from '@/components/ui/HowItWorks'
import { Notice } from '@/components/ui/Notice'
import { Panel } from '@/components/ui/Panel'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/StateMessage'
import { useMe } from '@/features/auth/MeContext'
import layout from '@/features/runway/components/Layout.module.css'
import { PageHead } from '@/features/runway/components/PageHead'

import styles from './Settings.module.css'
import { SageGroupRow, TileEditor } from './TileEditor'
import { TileModePanel } from './TileModePanel'
import {
  addTile,
  draftChanges,
  draftError,
  draftFrom,
  isDirty,
  mergeInto,
  moveTile,
  removeDept,
  removeTile,
  renameTile,
  sageGroups,
  type TileDraft,
  toggleHidden,
  toInput,
} from './tiles'
import { useDepartmentSettings, useSaveDepartmentSettings } from './useSettings'

const NO_INHERITED: readonly string[] = []

function DepartmentsHelp() {
  return (
    <HowItWorks
      storageKey="fpa.help.departments"
      automatic={
        <>
          Every Sage department gets counted. A department under a Sage parent rolls up into the parent&rsquo;s tile, so
          new sub-departments appear on the next sync with no change here. Departments inactive in Sage show only while
          they still have spend or budget.
        </>
      }
      here={
        <>
          Custom tiles for what Sage doesn&rsquo;t model: combine departments (like CO+MC), rename, reorder or hide. A
          department listed in a custom tile is counted there only — never twice.
        </>
      }
      who={<>FPA admins and managers. Saving changes the runway for everyone at once and is recorded with your name.</>}
    />
  )
}

function TilesEditor({ settings }: { settings: DepartmentSettings }) {
  const [draft, setDraft] = useState<TileDraft[]>(() => draftFrom(settings))
  const [flash, setFlash] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const save = useSaveDepartmentSettings()

  // A save (or a refetch) replaces the baseline: start the draft over from it.
  useEffect(() => setDraft(draftFrom(settings)), [settings])

  const departments = useMemo(() => new Map(settings.departments.map((d) => [d.id, d])), [settings.departments])
  const { groups, inherited } = useMemo(() => sageGroups(draft, settings.departments), [draft, settings.departments])
  const changes = useMemo(() => draftChanges(draft, settings), [draft, settings])
  const dirty = isDirty(draft, settings)
  const invalid = draftError(draft)
  const busy = save.isPending

  const edit = useCallback((change: (tiles: TileDraft[]) => TileDraft[]) => {
    setFlash(null)
    setDraft(change)
  }, [])
  const onRename = useCallback((code: string, name: string) => edit((d) => renameTile(d, code, name)), [edit])
  const onMove = useCallback((index: number, delta: -1 | 1) => edit((d) => moveTile(d, index, delta)), [edit])
  const onToggleHidden = useCallback((code: string) => edit((d) => toggleHidden(d, code)), [edit])
  const onRemoveDept = useCallback((code: string, dept: string) => edit((d) => removeDept(d, code, dept)), [edit])
  const onRemoveTile = useCallback((code: string) => edit((d) => removeTile(d, code)), [edit])
  const onAdd = useCallback((dept: SageDepartment) => edit((d) => addTile(d, dept)), [edit])
  const onMerge = useCallback((code: string, dept: string) => edit((d) => mergeInto(d, code, dept)), [edit])

  const { mutate, reset } = save
  const askSave = useCallback(() => {
    reset()
    setConfirming(true)
  }, [reset])
  const cancelSave = useCallback(() => setConfirming(false), [])
  const handleSave = useCallback(() => {
    setFlash(null)
    mutate(toInput(draft), {
      onSuccess: () => {
        setConfirming(false)
        setFlash('Department tiles saved. Everyone sees them on the runway now.')
      },
    })
  }, [mutate, draft])
  const handleDiscard = useCallback(() => {
    reset()
    setFlash(null)
    setDraft(draftFrom(settings))
  }, [reset, settings])

  const hierarchyCount = groups.filter((g) => g.grouping === 'sage_hierarchy').length

  return (
    <>
      {flash ? (
        <div className={styles.flash} role="status">
          <ICONS.check size={14} className={styles.flashIcon} />
          {flash}
        </div>
      ) : null}
      {save.isError && !confirming ? (
        <Notice tone="warn">{errorMessage(save.error, 'Could not save the tiles.')}</Notice>
      ) : null}

      <Panel
        title="Custom tiles (in FPA)"
        aside={
          <Badge tone="accent" small>
            {draft.length} {draft.length === 1 ? 'tile' : 'tiles'} · top to bottom = runway order
          </Badge>
        }
      >
        {draft.length === 0 ? (
          <EmptyState>
            No custom tiles: the runway follows Sage&rsquo;s departments and hierarchy as they are. Use &ldquo;Make
            custom tile&rdquo; below to combine or rename departments.
          </EmptyState>
        ) : (
          <ul className={styles.tileList}>
            {draft.map((tile, index) => (
              <TileEditor
                key={tile.code}
                tile={tile}
                inherited={inherited.get(tile.code) ?? NO_INHERITED}
                index={index}
                count={draft.length}
                departments={departments}
                disabled={busy}
                onRename={onRename}
                onMove={onMove}
                onToggleHidden={onToggleHidden}
                onRemoveDept={onRemoveDept}
                onRemoveTile={onRemoveTile}
              />
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Automatic from Sage"
        aside={
          <Badge tone={groups.length ? 'good' : 'neutral'} small>
            {groups.length} {groups.length === 1 ? 'tile' : 'tiles'}
            {hierarchyCount ? ` · ${hierarchyCount} from Sage hierarchy` : ''}
          </Badge>
        }
      >
        {groups.length === 0 ? (
          <EmptyState>Every Sage department is in a custom tile.</EmptyState>
        ) : (
          <>
            <p className={styles.sectionHint}>
              These follow Sage on their own: each top-level department gets a tile with its sub-departments rolled up.
              They show on the runway when they have spend or budget in the quarter. Make one a custom tile only to
              rename or combine it.
            </p>
            {groups.map((group) => (
              <SageGroupRow
                key={group.root}
                group={group}
                tiles={draft}
                disabled={busy}
                canEdit
                onAdd={onAdd}
                onMerge={onMerge}
              />
            ))}
          </>
        )}
      </Panel>

      <div className={styles.footer}>
        {dirty ? (
          <span className={styles.dirty}>{invalid ?? 'Unsaved changes'}</span>
        ) : (
          <span className={styles.consequence}>Saving changes the runway for everyone.</span>
        )}
        <button type="button" className={styles.secondary} onClick={handleDiscard} disabled={!dirty || busy}>
          Discard
        </button>
        <button
          type="button"
          className={styles.primary}
          onClick={askSave}
          disabled={!dirty || busy || invalid !== null}
        >
          <ICONS.check size={13} />
          Save tiles
        </button>
      </div>

      {confirming ? (
        <ConfirmDialog
          title="Save department tiles?"
          confirmLabel="Save for everyone"
          busy={busy}
          error={save.isError ? errorMessage(save.error, 'Could not save the tiles.') : null}
          onConfirm={handleSave}
          onCancel={cancelSave}
        >
          <p>The runway changes for everyone as soon as you save:</p>
          <ul>
            {changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
          <p>Totals do not change: every department is still counted exactly once.</p>
        </ConfirmDialog>
      ) : null}
    </>
  )
}

export function DepartmentsSettingsPage() {
  const me = useMe()
  const allowed = me.permissions.canConfigure
  const settings = useDepartmentSettings(allowed)
  const handleRetry = useCallback(() => {
    void settings.refetch()
  }, [settings])

  return (
    <div className={`${layout.view} ${layout.readable}`}>
      <PageHead
        title="Department tiles"
        sub="How Sage departments become runway tiles: Sage's hierarchy is followed automatically; custom tiles handle the exceptions."
        badge={null}
      />
      {!allowed ? (
        <ErrorState>
          Only FPA admins and managers can change department tiles. Ask an FPA admin or manager if a tile should be
          grouped or renamed.
        </ErrorState>
      ) : settings.isPending ? (
        <Skeleton label="Loading department settings" />
      ) : settings.error ? (
        <ErrorState>
          {errorMessage(settings.error, 'Could not load the department settings.')}{' '}
          <button type="button" className={styles.secondary} onClick={handleRetry}>
            <ICONS.refresh size={13} />
            Try again
          </button>
        </ErrorState>
      ) : (
        <>
          <DepartmentsHelp />
          <TileModePanel />
          <TilesEditor settings={settings.data} />
        </>
      )}
    </div>
  )
}
